// Lib
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Handsontable from 'handsontable/base';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';
import { registerEditor } from 'handsontable/editors';
import {
    AutoColumnSize,
    Autofill,
    ContextMenu,
    CustomBorders,
    Formulas,
    ManualColumnMove,
    ManualRowResize,
    ManualRowMove,
    MultipleSelectionHandles,
    registerPlugin,
    UndoRedo,
} from 'handsontable/plugins';

// Custom Plugins/Editors
import MilanoteFormulaCellReferencePlugin from './modules/MilanoteFormulaCellReferencePlugin';
import MilanoteFormulaAutocompletePlugin from './modules/MilanoteFormulaAutocompletePlugin';
import MilanoteEditingPlugin from './modules/MilanoteEditingPlugin';
import MilanoteEditingCleanupPlugin from './modules/MilanoteEditingCleanupPlugin';
import MilanoteContextMenuPlugin, { MILANOTE_CONTEXT_MENU_PLUGIN_NAME } from './modules/MilanoteContextMenuPlugin';
import MilanoteManualColumnResizePlugin from './modules/MilanoteManualColumnResizePlugin';
import MilanoteCopyPastePlugin from './modules/MilanoteCopyPastePlugin';

import MilanoteCellEditor from './modules/MilanoteCellEditor';
import MilanoteCellEditorComponent from './modules/MilanoteCellEditorComponent';

import hyperFormulaInstance from './manager/hyperFormulaInstance';

// Config
import getClientConfig from '../../utils/getClientConfig';

// Components
import useTableCellEditingHandlers from './handlers/useTableCellEditingHandlers';
import TableOverlay from './handlers/TableOverlay';
import TablePopups from './TablePopups';
import HotTableRendererPortalManager from './manager/HotTableRendererPortalManager';
import useTableStateSyncHandlers from './handlers/useTableStateSync';
import useInitTableInstanceApi from './handlers/useInitTableInstanceApi';
import useTableSelectionHandlers from './handlers/useTableSelectionHandlers';
import useTableColumnResizeHandlers from './handlers/useTableColumnResizeHandlers';
import useTableResizeEffects from './handlers/useTableResizeEffects';
import useTableMoveColumnRowHandlers from './handlers/useTableMoveColumnRowHandlers';
import useTableContextMenuHandlers from './handlers/useTableContextMenu';
import useTableSelectionEffects from './handlers/useTableSelectionEffects';
import useTableColumnResizeEffects from './handlers/useTableColumnResizeEffects';
import useTableStateSyncEffects from './handlers/useTableStateSyncEffects';
import useTableCellEditingEffects from './handlers/useTableCellEditingEffects';
import { useSyncActiveTableState } from './useSyncActiveTableState';
import { useTableEditingHandlers } from './handlers/useTableEditingHandlers';

// Utils
import { markEventAsHandled } from '../../utils/react/reactEventUtil';
import { getElementId } from '../../../common/elements/utils/elementPropertyUtils';
import { getCellValueArray } from './utils/tableDataUtils';
import { convertColWidthsGUtoPx, getColWidthsResizeWidth } from './utils/tableSizeUtils';

// Selectors
import {
    getTableContentColWidthsGUSelector,
    getTableContentDataSelector,
    isCurrentlyEditingTableCell as selectIsCurrentlyEditingTableCell,
} from './tableSelector';
import { getThemeIsDarkMode } from '../../user/account/accountModal/interface/themeSettings/themeSelector';
import { getCurrentlyEditingEditorIdForElement } from '../selection/currentlyEditingSelector';
import { getIsRedoing, getIsUndoing } from '../../utils/undoRedo/undoRedoSelector';
import { userCurrencyPreferenceSelector, userLanguagePreferenceSelector } from '../../user/currentUserSelector';

// Types
import { TableAxis } from '../../../common/table/TableTypes';

// Styles
import './HotTable.scss';
import './HotTableRefHeaders.scss';
import './HotContextMenu.scss';

const config = getClientConfig();

// Register Handonstable plugins
registerPlugin(ContextMenu);
registerPlugin(AutoColumnSize);
registerPlugin(ManualRowResize);
registerPlugin(ManualColumnMove);
registerPlugin(ManualRowMove);
registerPlugin(Formulas);
registerPlugin(CustomBorders);
registerPlugin(UndoRedo);
registerPlugin(Autofill);
registerPlugin(MultipleSelectionHandles);

// Register Hansontable custom plugins
registerPlugin(MilanoteManualColumnResizePlugin);
registerPlugin(MilanoteFormulaCellReferencePlugin);
registerPlugin(MilanoteFormulaAutocompletePlugin);
registerPlugin(MilanoteEditingPlugin);
registerPlugin(MilanoteEditingCleanupPlugin);
registerPlugin(MilanoteContextMenuPlugin);
registerPlugin(MilanoteCopyPastePlugin);

// Register Hansontable custom editor
registerEditor('milanoteCellEditor', MilanoteCellEditor);

// FIXME - It looks like this creates a new selector on every call, which effectively disables memoization.
//  Instead if it used a "createMapStateToProps" pattern that returned a new function it would work correctly.
const mapStateToProps = (state, { elementId }) =>
    createStructuredSelector({
        tableContentData: getTableContentDataSelector(),
        tableContentColWidthsGU: getTableContentColWidthsGUSelector(),
        currentlyEditingEditorId: getCurrentlyEditingEditorIdForElement(elementId),
        isCurrentlyEditingTableCell: selectIsCurrentlyEditingTableCell(elementId),
        isDarkMode: getThemeIsDarkMode,
        isUndoingOrRedoing: (state) => getIsUndoing(state) || getIsRedoing(state),
        locale: userLanguagePreferenceSelector,
        currencyPreference: userCurrencyPreferenceSelector,
    });

const HotTable = (props) => {
    const {
        element,
        elementId,
        tableContentData,
        tableContentColWidthsGU,
        hotTableContainerRef,
        hotTableInstanceRef,
        gridSize,
        isDarkMode,
        inList,
        isReadOnly,
        isResizing,
        isCurrentlyEditingTableCell,
        getContextZoomScale,
        showTitle,
        showCaption,
        locale,
        filterQuery,
        isSingleSelected,
        currentCellSelections,
        isEditing,
        dispatchStartEditingTitle,
        dispatchStartEditingTableCell,
        dispatchStartEditingTableGrid,
    } = props;

    const [mounted, setMounted] = useState(false);
    const [currentlyEditingCellEditorCoords, setCurrentlyEditingCellEditorCoords] = useState(null);

    const dispatch = useDispatch();
    const cellEditorRef = useRef(null);
    const dropdownRef = useRef(null);

    const hotTableRendererPortalManagerRef = useRef(null);

    const { jumpToTitle, startEditingCellEditor, finishEditingCellEditor } = useTableEditingHandlers({
        hotTableInstanceRef,
        showTitle,
        elementId,
        isCurrentlyEditingTableCell,
        dispatchStartEditingTitle,
        dispatchStartEditingTableCell,
        dispatchStartEditingTableGrid,
        setCurrentlyEditingCellEditorCoords,
    });

    useInitTableInstanceApi(props);

    const tableCellEditingHandlers = useTableCellEditingHandlers(props);
    const tableCellSelectionHandlers = useTableSelectionHandlers(props);
    const tableColumnResizeHandlers = useTableColumnResizeHandlers(props);
    const tableMoveColumnRowHandlers = useTableMoveColumnRowHandlers(props);
    const tableContextMenuHandlers = useTableContextMenuHandlers(props);
    const tableStateSyncHandlers = useTableStateSyncHandlers(props);

    // Initialize Handsontable instance
    useEffect(() => {
        const tableContainer = hotTableContainerRef.current;
        const resizeToWidth = getColWidthsResizeWidth(inList, hotTableContainerRef, getContextZoomScale);
        const tableContentColWidthsPx = convertColWidthsGUtoPx(tableContentColWidthsGU, gridSize, resizeToWidth);

        hotTableInstanceRef.current = new Handsontable(tableContainer, {
            licenseKey: config.handsontable?.licenseKey,
            data: getCellValueArray(tableContentData),

            editor: isReadOnly ? null : 'milanoteCellEditor',
            renderer: hotTableRendererPortalManagerRef.current.getRendererWrapper(),

            colHeaders: true,
            rowHeaders: true,
            autoColumnSize: false,
            autoRowSize: false,
            manualRowMove: true,
            manualColumnMove: true,
            columnHeaderHeight: 0,
            rowHeaderHeight: 0,
            height: 'auto',
            formulas: { engine: hyperFormulaInstance, sheetName: elementId },
            manualColumnResize: tableContentColWidthsPx,
            manualRowResize: true,
            contextMenu: tableContextMenuHandlers.getTableContextMenuConfig,
            outsideClickDeselects: false,
            colWidths: tableColumnResizeHandlers.getDefaultColWidth,
            rowHeights: tableColumnResizeHandlers.getDefaultRowHeight,

            // NOTE: Needs to be in the function format to avoid the `this` context being lost
            afterInit() {
                // This is a hack to get the table to get handsontable context menu event only triggered when there are
                // cell selections
                this.getPlugin(MILANOTE_CONTEXT_MENU_PLUGIN_NAME).removeHotContextMenuEventListeners();

                // This is a hack to force handsontable to open keyboard on cell selection on the iPad.
                // Handsontable, by default, does not do this: https://github.com/handsontable/handsontable/pull/5529
                const copyPastePluginInstance = this.getPlugin('CopyPaste');
                if (copyPastePluginInstance?.focusableElement?.focus && !isReadOnly) {
                    copyPastePluginInstance.focusableElement.focus = function () {
                        this.mainElement.value = ' ';
                        this.mainElement.select();
                    };
                }

                // This is a hack to force remove the minimum row height of 23px that handsontable sets
                // when setting row height
                const manualRowResizePluginInstance = this.getPlugin('manualRowResize');
                if (manualRowResizePluginInstance) {
                    manualRowResizePluginInstance.setManualSize = function (row, height) {
                        const physicalRow = this.hot.toPhysicalRow(row);
                        this.rowHeightsMap.setValueAtIndex(physicalRow, height);
                        return height;
                    };
                }
            },

            afterViewRender: (...args) => hotTableRendererPortalManagerRef.current.afterViewRender(...args),

            beforeChange: tableCellEditingHandlers.beforeChange,
            afterChange: tableCellEditingHandlers.afterChange,
            afterCreateRow: tableCellEditingHandlers.afterGridInsert(TableAxis.ROW),
            afterCreateCol: tableCellEditingHandlers.afterGridInsert(TableAxis.COL),
            afterRemoveRow: tableCellEditingHandlers.afterGridRemove(TableAxis.ROW),
            afterRemoveCol: tableCellEditingHandlers.afterGridRemove(TableAxis.COL),

            beforeAutofill: tableCellEditingHandlers.beforeAutofill,

            beforeCopy: tableCellEditingHandlers.beforeCopy,
            beforeCut: tableCellEditingHandlers.beforeCut,
            beforePaste: tableCellEditingHandlers.beforePaste,

            beforeOnCellMouseDown: (...args) => {
                tableCellEditingHandlers.beforeOnCellMouseDown(...args);
                tableMoveColumnRowHandlers.beforeOnCellMouseDown(...args);
            },

            afterRowMove: tableCellEditingHandlers.afterGridMovement(TableAxis.ROW),
            afterColumnMove: tableCellEditingHandlers.afterGridMovement(TableAxis.COL),

            afterSelection: tableCellSelectionHandlers.afterSelection,
            afterSelectionEnd: tableCellSelectionHandlers.afterSelectionEnd,
            afterDeselect: tableCellSelectionHandlers.afterDeselect,

            afterColumnResize: tableColumnResizeHandlers.afterColumnResize,

            cellEditorRef,
            milanoteEditing: {
                jumpToTitle,
                isReadOnly,
                elementId,
                dispatch,
            },
            milanoteFormulaCellReference: {
                cellEditorRef,
            },
            milanoteFormulaAutocomplete: {
                enabled: true,
                cellEditorRef,
                dropdownRef,
            },
        });

        hotTableInstanceRef.current.milanoteProps = {
            gridSize,
            isDarkMode,
            showTitle,
            showCaption,
            getContextZoomScale,
            hotTableContainerRef,
            isResizing,
            elementId: getElementId(element),
            locale,
            filterQuery,
            startEditingCellEditor,
            finishEditingCellEditor,
        };

        setMounted(true);

        return () => {
            // Destroy the hot table hooks
            Handsontable.hooks.destroy(hotTableInstanceRef.current);

            // Destroy the hot table instance
            // This is done in a setTimeout to ensure that the hot table instance is destroyed after
            // the hot table is finished moving (this happens when the table is being moved to a new location)
            setTimeout(() => {
                hotTableInstanceRef.current?.destroy();
            });
        };
    }, []);

    useSyncActiveTableState(hotTableInstanceRef.current, isEditing);

    // These need to be called after the effect that initializes the hotTableInstanceRef
    useTableCellEditingEffects(props);
    useTableSelectionEffects(props);
    useTableColumnResizeEffects(props);
    useTableResizeEffects(props);
    useTableStateSyncEffects({ ...props, tableStateSyncHandlers });

    const onMouseDown = useCallback(
        (event) => {
            // This is done in order to stop the parent element (in ElementContainer) to handle this event when other areas of
            // the table is clicked.
            isSingleSelected && markEventAsHandled(event);
        },
        [isSingleSelected],
    );

    const nRows = hotTableInstanceRef.current?.countRows();
    const nCols = hotTableInstanceRef.current?.countCols();

    const isSelectedLastRow = useMemo(
        () =>
            !!currentCellSelections &&
            currentCellSelections.some(([startRow, , endRow]) => startRow === nRows - 1 || endRow === nRows - 1),
        [currentCellSelections, nRows],
    );

    const isSelectedLastCol = useMemo(
        () =>
            !!currentCellSelections &&
            currentCellSelections.some(([, startCol, , endCol]) => startCol === nCols - 1 || endCol === nCols - 1),
        [currentCellSelections, nCols],
    );

    return (
        <div
            className={classNames('HotTable', {
                'is-selected-last-col': isSelectedLastCol,
                'is-selected-last-row': isSelectedLastRow,
            })}
            onMouseDown={onMouseDown}
        >
            <TableOverlay {...props} />

            <TablePopups {...props} dropdownRef={dropdownRef} cellEditorRef={cellEditorRef} />

            <MilanoteCellEditorComponent
                {...props}
                cellEditorRef={cellEditorRef}
                hotTableContainerRef={hotTableContainerRef}
                hotTableInstanceRef={hotTableInstanceRef}
                gridSize={gridSize}
                currentlyEditingCellEditorCoords={currentlyEditingCellEditorCoords}
                // Pass in mounted prop to ensure that the MilanoteCellEditorComponent
                // is re-rendered after HotTable is mounted
                mounted={mounted}
            />

            <HotTableRendererPortalManager ref={hotTableRendererPortalManagerRef} />

            <div className="HotTableLib" ref={hotTableContainerRef}></div>
        </div>
    );
};

HotTable.propTypes = {
    element: PropTypes.object,
    elementId: PropTypes.string,
    tableContentData: PropTypes.array,
    tableContentColWidthsGU: PropTypes.array,
    dispatchUpdateTableElement: PropTypes.func,
    gridSize: PropTypes.number,
    tempSize: PropTypes.object,
    currentCellSelections: PropTypes.object,
    isPresentational: PropTypes.bool,
    isSingleSelected: PropTypes.bool,
    isEditing: PropTypes.bool,
    isResizing: PropTypes.bool,
    isDarkMode: PropTypes.bool,
    inList: PropTypes.string,
    isReadOnly: PropTypes.bool,
    showTitle: PropTypes.bool,
    showCaption: PropTypes.bool,
    isCurrentlyEditingTableCell: PropTypes.bool,
    locale: PropTypes.string,
    filterQuery: PropTypes.string,
    translateTableContextMenu: PropTypes.func,

    dispatchSetCellSelection: PropTypes.func,
    dispatchStartEditingTableCell: PropTypes.func,
    dispatchFinishEditingElement: PropTypes.func,

    dispatchUndoAction: PropTypes.func,
    dispatchRedoAction: PropTypes.func,
    dispatchDeselectElement: PropTypes.func,
    dispatchStartEditingTitle: PropTypes.func,
    dispatchStartEditingTableGrid: PropTypes.func,

    hotTableInstanceRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    hotTableContainerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    getContextZoomScale: PropTypes.func,
};

export default connect(mapStateToProps, null, null)(HotTable);
